import React, { useCallback } from 'react';
import { Button, HStack, VStack } from '@chakra-ui/react';

import { useFilters, updateQueryParams } from '../../../../lib';
import { FilterComponent, LocationFilters, SearchFilter } from '.';
import { __ } from '../../../../lib/i18n';

interface IRenderFiltersProps {
	isVisibleOnView?: boolean;
	isDefaultFiltersOpened?: boolean;
	setAreFiltersApplied?: React.Dispatch<React.SetStateAction<boolean>>;
	onClose?: () => void;
}

const filterConfig = [
	{
		filterType: 'EventLocations',
		label: 'Venue',
		prop: 'eventLocations',
	},
	{
		filterType: 'EventTypes',
		label: 'Category',
		prop: 'eventTypes',
	},
	{
		filterType: 'EventTags',
		label: 'Tag',
		prop: 'eventTags',
	},
];

export const RenderFilters = ({
	isVisibleOnView,
	isDefaultFiltersOpened = true,
	setAreFiltersApplied,
	onClose,
}: IRenderFiltersProps) => {
	const { events, filters, defaultFilters, setFilters, setFilteredEvents, setSearchTerm, handleApplyFilters } =
		useFilters();

	const handleApply = useCallback(() => {
		handleApplyFilters();
		if (setAreFiltersApplied) {
			setAreFiltersApplied(true);
		}
		if (onClose) {
			onClose();
		}
	}, [setAreFiltersApplied, onClose, handleApplyFilters]);

	const handleResetFilters = useCallback(() => {
		const emptyFilters = {
			selectedEventTypes: [],
			selectedEventTags: [],
			selectedEventLocations: [],
			selectedCountries: [],
			selectedStates: [],
			selectedCities: [],
		};
		setFilters({
			...filters,
			...emptyFilters,
		});
		setFilteredEvents(events);
		setSearchTerm('');
		updateQueryParams('', emptyFilters);
		if (setAreFiltersApplied) {
			setAreFiltersApplied(false);
		}
		if (onClose) {
			onClose();
		}
	}, [events, filters, setFilters, setFilteredEvents, setSearchTerm, setAreFiltersApplied, onClose]);

	const renderButtons = (
		<HStack w='full' alignItems='baseline' pb={6}>
			<Button
				colorScheme='blue'
				variant='solid'
				size='md'
				borderRadius='md'
				fontSize='xs'
				w='full'
				onClick={handleApply}
			>
				{__('Apply Filters')}
			</Button>
			<Button
				variant='outline'
				colorScheme='red'
				size='md'
				borderRadius='md'
				fontSize='xs'
				w='full'
				onClick={handleResetFilters}
			>
				{__('Reset')}
			</Button>
		</HStack>
	);

	const renderFilterComponents = () =>
		filterConfig.map(({ filterType, label, prop }) => (
			<FilterComponent
				key={filterType}
				filterType={filterType as 'EventLocations' | 'EventTypes' | 'EventTags'}
				label={label}
				defaultOptions={defaultFilters[prop]}
				selectedOptions={filters[`selected${prop.charAt(0).toUpperCase() + prop.slice(1)}`]}
				isDefaultOpened={isDefaultFiltersOpened}
			/>
		));

	return (
		<VStack paddingBlock={isVisibleOnView ? 4 : 16} height='full' justifyContent='space-between' w='full'>
			<VStack w='full' spacing={4}>
				<SearchFilter />
				<LocationFilters />
				{renderFilterComponents()}
			</VStack>
			{renderButtons}
		</VStack>
	);
};
